<template>
  <dialogue>
    <submission-confirm
      v-if="file && !hasError"
      :img="imageSource"
      :uploading="uploading"
      @cancel="cancelUpload"
      @handleSubmit="submitHandler"
    />

    <div v-else class="drop-area" ref="dragArea" :class="dropAreaClass">
      <img class="mb-2" src="~@/assets/images/document-icon.svg" alt="" />
      <span class="h3 mb-5">Drop your art here</span>

      <label class="file-input" for="newSubmission">
        <app-button class="fake" size="sm">Or browse your files...</app-button>
        <input
          type="file"
          @change="updateFile"
          name="newSubmission"
          id="newSubmission"
          accept=".jpg,.jpeg,.png"
        />
      </label>

      <div class="directions">
        <div class="directions__error" v-if="hasError">
          <span class="error">
            File size is too large ({{ (file.size / 1000000).toFixed(2) }}MB).
          </span>
          <span class="small" v-if="!subscription">
            Upgrade your twitch subscription for 2MB file limit
          </span>
          <span
            class="small"
            v-if="subscription === 'Prime' || subscription === '1000'"
          >
            Upgrade your twitch subscription for 6MB file limit
          </span>
          <span class="small" v-if="subscription === '2000'">
            Upgrade your twitch subscription for 10MB file limit
          </span>
        </div>
        <template v-else>
          <span>Only JPG and PNG files are allowed</span>
          <span>Max {{ maxFileSize }}</span>
        </template>
      </div>
    </div>
  </dialogue>
</template>

<script>
import Dialogue from '@/components/global/Dialogue';
import SubmissionConfirm from './SubmissionConfirm';
import { mapActions, mapState } from 'vuex';

export default {
  name: 'new-submission',
  components: { Dialogue, SubmissionConfirm },
  data: function() {
    return {
      dropAreaClass: '',
      file: undefined,
      reader: undefined,
      imageSource: null,
      uploading: false,
      uploaded: false,
    };
  },
  created() {
    this.reader = new FileReader();
    this.reader.onloadend = () => {
      this.imageSource = this.reader.result;
    };
  },
  watch: {
    file(newVal) {
      if (!newVal) return;
      this.reader.readAsDataURL(newVal);
    },
  },
  computed: {
    ...mapState('auth', {
      subscription: state => state.user.subscription,
    }),
    ...mapState('uploadSubmission', {
      comment: state => state.comment,
    }),
    maxFileSize() {
      switch (this.subscription) {
        case '1000':
        case 'Prime':
          return '2MB';
        case '2000':
          return '6MB';
        case '3000':
          return '10MB';
        default:
          return '500KB';
      }
    },
    hasError() {
      let error = false;
      switch (this.subscription) {
        case '1000':
          if (this?.file?.size > 2000000) error = true;
          break;
        case '2000':
          if (this?.file?.size > 6000000) error = true;
          break;
        case '3000':
          if (this?.file?.size > 1000000) error = true;
          break;
        default:
          if (this?.file?.size > 500000) error = true;
          else error = false;
      }
      return error;
    },
  },
  mounted() {
    const dragArea = this.$refs.dragArea;
    if (this.file) {
      this.imageSource = this.reader;
    }
    const preventDefaults = function(e) {
      e.preventDefault();
      e.stopPropagation();
    };

    dragArea.addEventListener('dragenter', preventDefaults, false);
    dragArea.addEventListener('dragleave', preventDefaults, false);
    dragArea.addEventListener('dragover', preventDefaults, false);
    dragArea.addEventListener('drop', preventDefaults, false);

    dragArea.addEventListener('dragenter', this.highlight, false);
    dragArea.addEventListener('dragleave', this.unhighlight, false);
    dragArea.addEventListener('dragover', this.highlight, false);
    dragArea.addEventListener('drop', this.handleDrop, false);
  },

  methods: {
    ...mapActions('mySubmissions', ['addSubmission']),
    ...mapActions('uploadSubmission', ['setStatus']),
    highlight() {
      if (this.dropAreaClass !== 'entered') {
        this.dropAreaClass = 'entered';
      }
    },
    unhighlight() {
      this.dropAreaClass = 'default';
    },
    handleDrop(e) {
      this.$gtag.event('drag_drop_submission', {
        event_category: 'participation',
        event_label: 'Use drag and drop to submit',
      });
      let dt = e.dataTransfer;
      const file = dt.files[0];
      this.file = file;
      this.$emit('newFile', file);
      this.unhighlight();
    },
    updateFile(e) {
      const file = e.target.files[0];
      this.file = file;
      this.$emit('newFile', file);
    },
    cancelUpload() {
      this.file = undefined;
      this.imageSource = '';
      this.unhighlight();
    },
    async submitHandler() {
      this.uploading = true;
      const data = new FormData();

      data.append('submission', this.file, +new Date());
      data.append('critique', false);
      data.append('public', true);
      data.append('comment', this.comment);

      await this.addSubmission({
        id: this.$route.params.id,
        data,
      });
      this.setStatus('success');
      this.uploading = false;
    },
  },
  filters: {
    fileSize(b) {
      const mb = (b / 1000000).toFixed(2);
      const kb = (b / 1000).toFixed();

      if (mb < 1) {
        return `${kb}kb`;
      }

      return `${mb}MB`;
    },
  },
};
</script>

<style lang="scss" scoped>
.drop-area {
  border: 2px dashed color('secondary');
  width: 100%;
  padding: 3rem 1rem;
  border-radius: 1rem;
  display: flex;
  flex-direction: column;

  &.entered {
    background: rgba(color('primary'), 0.2);
    border-width: 2px;
    .file-input {
      opacity: 0.2;
    }
  }

  .directions {
    display: flex;
    justify-content: space-between;
    margin-top: 2.5rem;
    width: 100%;
    margin-bottom: -2.5rem;
    font-size: calc-font-size(16);
    color: color('text', 'light');

    &__error {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      width: 100%;
    }
  }

  .fake {
    pointer-events: none;
  }

  .error {
    animation-name: wiggle;
    animation-duration: 100ms;
    animation-iteration-count: 2;
  }

  .file-input {
    width: 100%;
    height: 100%;
    cursor: pointer;
    text-align: center;
    background: rgba(255, 255, 255, 0);
    transition: all 128ms ease-in-out;
    display: flex;
    justify-content: center;
    align-items: center;
    opacity: 1;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    input[type='file'] {
      display: none;
    }
  }
}

@keyframes wiggle {
  0% {
    transform: translateX(0);
  }
  20% {
    transform: translateX(3px);
  }
  40% {
    transform: translateX(0);
  }
  60% {
    transform: translateX(-3px);
  }
  80% {
    transform: translateX(0);
  }
}
</style>
